import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Avatar,
  Paper,
  Stack,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BusinessIcon from '@mui/icons-material/Business';
import LabelIcon from '@mui/icons-material/Label';
import { request } from '../../../../../utils/request';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { Brand, BrandFormData } from '../../../types';
import { baseUrlImage } from '../../../../../utils/apiClient';
import { useImageCompression } from '../../../../../utils/useImageCompression';

const manufactureTypeOptions = ['Cars', 'Parts'];

const EditBrand: React.FC = () => {
  const { compressSingleImage } = useImageCompression();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [existingBrand, setExistingBrand] = useState<Brand | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BrandFormData>({
    defaultValues: {
      name: '',
      parentCompany: '',
      description: '',
      manufactureType: [],
      brandLogo: null,
    },
  });

  const watchedLogo = watch('brandLogo');
  const watchedManufactureType = watch('manufactureType');

  // Fetch brand data when component mounts
  useEffect(() => {
    const fetchBrand = async () => {
      try {
        setFetchLoading(true);
        const res = await request({
          url: `brand/get-brand/${id}`,
          method: 'get',
        });

        if (res.status === 'success' && res.brand) {
          const brand = res.brand;
          setExistingBrand(brand);

          // Pre-fill form with existing data
          reset({
            name: brand.name || '',
            parentCompany: brand.parentCompany || '',
            description: brand.description || '',
            manufactureType: brand.manufactureType || [],
            brandLogo: null,
          });

          // Set logo preview with existing logo
          if (brand.brandLogo) {
            setLogoPreview(baseUrlImage + brand.brandLogo);
          }
        } else {
          toast.error('Brand not found');
          navigate('/configuration/brands');
        }
      } catch (error: any) {
        console.error('Error fetching brand:', error);
        toast.error('Failed to load brand data');
        navigate('/configuration/brands');
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchBrand();
    }
  }, [id, navigate, reset]);

  // const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //     setValue('brandLogo', file, { shouldValidate: true });

  //     // Create preview URL
  //     const previewUrl = URL.createObjectURL(file);
  //     setLogoPreview(previewUrl);
  //   }
  // };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      const compressedFile = await compressSingleImage(file);

      setValue('brandLogo', compressedFile, { shouldValidate: true });

      const previewUrl = URL.createObjectURL(compressedFile);
      setLogoPreview(previewUrl);

      return () => {
        URL.revokeObjectURL(previewUrl);
      };
    } catch (error) {
      console.error('Compression error:', error);
      toast.error('Failed to compress image. Please try again.');
      setValue('brandLogo', file, { shouldValidate: true });
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleManufactureTypeChange = (event: any) => {
    const value = event.target.value;
    const selectedTypes = typeof value === 'string' ? value.split(',') : value;
    setValue('manufactureType', selectedTypes, { shouldValidate: true });
  };

  const removeManufactureType = (typeToRemove: string) => {
    const currentTypes = watchedManufactureType || [];
    const updatedTypes = currentTypes.filter(type => type !== typeToRemove);
    setValue('manufactureType', updatedTypes, { shouldValidate: true });
  };

  const onSubmit = async (data: BrandFormData) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description || '');
      formData.append('parentCompany', data.parentCompany || '');

      // Send manufacture types as individual form data fields
      data.manufactureType.forEach((type) => {
        formData.append('manufactureType', type);
      });

      // Only append brandLogo if a new one was selected
      if (data.brandLogo) {
        formData.append('brandLogo', data.brandLogo);
      }

      const res = await request({
        url: `brand/update-brand/${id}`,
        method: 'put',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.status === 'success') {
        toast.success('Brand updated successfully!');
        navigate('/configuration/brands');
      } else {
        toast.error(res.message || 'Failed to update brand');
      }
    } catch (error: any) {
      console.error('Update brand error:', error);
      toast.error(error || 'Error updating brand');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Card elevation={4} sx={{ borderRadius: 3, maxWidth: 1200, mx: 'auto' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading brand details...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Card elevation={4} sx={{ borderRadius: 1, maxWidth: 1200, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
              Edit Brand
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Update the details below to modify the brand information
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>

              {/* Side by side layout for Logo and Information */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>

                {/* Logo Section - Left Side */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 1, flex: 1, backgroundColor: "#f6f8fb" }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Brand Logo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {existingBrand?.brandLogo
                        ? 'Update your brand logo image'
                        : 'Upload your brand logo image'
                      }
                    </Typography>
                  </Box>

                  <Stack spacing={3} alignItems="center">
                    {/* Logo Preview */}
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        sx={{
                          width: 160,
                          height: 160,
                          bgcolor: 'grey.50',
                          border: '2px dashed',
                          borderColor: logoPreview ? 'primary.main' : 'grey.300',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        {logoPreview ? (
                          <Box
                            component="img"
                            src={logoPreview || (baseUrlImage + logoPreview)}
                            alt="Brand logo preview"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              p: 2
                            }}
                          />
                        ) : (
                          <BusinessIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                        )}
                      </Avatar>
                      {!logoPreview && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textAlign="center"
                          sx={{
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                          }}
                        >
                          {existingBrand?.brandLogo ? 'Current logo' : 'Logo preview will appear here'}
                        </Typography>
                      )}
                    </Box>

                    {/* Upload Controls */}
                    <Box sx={{ width: '100%' }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        color={watchedLogo ? "success" : "primary"}
                        fullWidth
                        sx={{
                          mb: 1,
                          py: 1
                        }}
                      >
                        {watchedLogo ? 'Change Logo' : (existingBrand?.brandLogo ? 'Replace Logo' : 'Choose Logo File')}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleLogoChange}
                        />
                      </Button>

                      {watchedLogo ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="success.main" display="block" fontWeight={500}>
                            ✓ {watchedLogo.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            New logo ready to upload
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                          {existingBrand?.brandLogo
                            ? 'Keep current logo or upload a new one. Supported formats: JPG, PNG, WebP'
                            : 'Supported formats: JPG, PNG, WebP • Max size: 5MB'
                          }
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>

                {/* Information Section - Right Side */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 1, flex: 1, backgroundColor: "#f6f8fb" }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Brand Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Update brand information and specifications
                    </Typography>
                  </Box>

                  <Stack spacing={3}>
                    {/* Brand Name */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                        Brand Name *
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="e.g., Toyota, Honda, BMW"
                        error={!!errors.name}
                        helperText={errors.name?.message || "Enter the brand name as it should appear in the system"}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LabelIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        {...register('name', {
                          required: 'Brand name is required',
                          minLength: { value: 2, message: 'Brand name must be at least 2 characters' }
                        })}
                      />
                    </Box>

                    {/* Parent Company */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                        Parent Company
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="e.g., Toyota Motor Corporation (optional)"
                        error={!!errors.parentCompany}
                        helperText={errors.parentCompany?.message || "Enter the parent company if applicable"}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        {...register('parentCompany')}
                      />
                    </Box>

                    {/* Manufacture Type */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                        Manufacture Types *
                      </Typography>
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.manufactureType}
                      >
                        <InputLabel>Select Mfg... types</InputLabel>
                        <Select
                          multiple
                          value={watchedManufactureType || []}
                          label="Select manufacture types"
                          onChange={handleManufactureTypeChange}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  color={value === 'Cars' ? 'primary' : 'secondary'}
                                  onDelete={() => removeManufactureType(value)}
                                  onMouseDown={(event) => event.stopPropagation()}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {manufactureTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                              <Checkbox checked={watchedManufactureType?.indexOf(type) > -1} />
                              <ListItemText primary={type} />
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.manufactureType ? (
                          <FormHelperText error>{errors.manufactureType.message}</FormHelperText>
                        ) : (
                          <FormHelperText>
                            Choose the types of products this brand manufactures
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    {/* Description */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                        Description
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Description (optional)"
                        error={!!errors.description}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DescriptionIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        {...register('description')}
                      />
                    </Box>

                  </Stack>
                </Paper>
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                <Button
                  variant="outlined"
                  size='small'
                  onClick={() => navigate('/configuration/brands')}
                  disabled={loading}
                  sx={{ minWidth: 100 }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  size='small'
                  variant="contained"
                  color="primary"
                  disabled={loading ? true : false}
                  sx={{ minWidth: 140 }}
                >
                  {loading ? <CircularProgress size={"20px"} sx={{ color: "white" }} aria-label="Updating..." /> : 'Update Brand'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default EditBrand;